import logging from '@sstdev/lib_logging';
import { hooks } from 'lib_ui-primitives';
// Aliased: `filters` is already used inside addAnyFiltersNeeded for this boundary's
// listening-filter registrations, which are a different thing entirely.
import { eventSink, filters as backboneFilters } from 'lib_ui-services';
import lodash from 'lodash';
import PropTypes from 'prop-types';
import {
    createContext,
    memo,
    createElement as rc,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef
} from 'react';
import useEventSink from '../../hooks/useEventSink';
import { EventBoundaryContext } from './EventBoundary';
const { set } = lodash;
const { get } = lodash;
const { useRouter } = hooks;

export const FilterInterdependencyBoundaryContext = createContext();
export const useFilterInterdependencyBoundary = () => useContext(FilterInterdependencyBoundaryContext);

const _p = {
    useRouter
};
export const _private = _p;
function FilterInterdependencyBoundaryProvider(props) {
    const { name, children, restoreSelectionsOnRemount = false } = props || {};
    const listeningFilters = useRef({});
    const parentSink = useEventSink();
    const { getRouteState, setRouteState } = _p.useRouter();
    const routeStorageKey = `listenersFor${name}`;
    const setSelection = useCallback(
        (value, namespace, relation) => {
            // lazily add listeners to the route specific storage
            const listeners = getRouteState(routeStorageKey, {});
            logging.debug(
                `[FilterInterdependencyBoundary] ${name}- setting for ${namespace}-${relation}: ${JSON.stringify(
                    value
                )}`
            );
            set(listeners, [namespace, relation], value);
        },
        [routeStorageKey, getRouteState, name]
    );

    const getSelection = useCallback(
        (namespace, relation) => {
            const listeners = getRouteState(routeStorageKey, {});
            const value = get(listeners, [namespace, relation]);
            logging.debug(
                `[FilterInterdependencyBoundary] ${name}- getting for ${namespace}-${relation}: ${JSON.stringify(
                    value
                )}`
            );
            return value;
        },
        [routeStorageKey, getRouteState, name]
    );

    const getAllSelections = useCallback(() => {
        return getRouteState(routeStorageKey, {});
    }, [getRouteState, routeStorageKey]);

    // When returning to this boundary, recreate any view filters for selections that were
    // retained in route state.
    useEffect(() => {
        addAnyFiltersNeeded();
        // Only do this when mounting.
        // eslint-disable-next-line
    }, []);

    const filterInterdependencyBoundaryContext = useMemo(
        () => ({
            name,
            getSelection,
            addFilterMetadata: (view, filters) => {
                let viewFiltersChanged = false;
                // Currently, foreignRelationSelectionOnForm is the only filter type that
                // requires a boundary to separate controls that are dependent on each other.
                const foreignRelationSelectionOnForm = filters.foreignRelationSelectionOnForm;
                if (foreignRelationSelectionOnForm != null) {
                    const { namespace, relation, id: filterId } = foreignRelationSelectionOnForm;
                    logging.debug(
                        `[FilterInterdependencyBoundary] ${name}- filter added for ${namespace}/${relation} on view for ${view.namespace}/${view.relation}(view name: ${view.name})`
                    );
                    // listeningFilters tracks which views depend on a given namespace/relation selection.
                    // Multiple views can share the same namespace/relation key (e.g. the same parent
                    // dropdown driving two different child dropdowns), so we store an array.
                    // If a matching entry already exists without viewCriteria (registered before the
                    // view fully mounted), replace it with the richer entry that has viewCriteria so
                    // addAnyFiltersNeeded can apply the correct filter. Otherwise, append a new entry.
                    const existingFilters = listeningFilters.current[namespace]?.[relation];
                    const normalizedFilters = Array.isArray(existingFilters)
                        ? existingFilters
                        : existingFilters
                          ? [existingFilters]
                          : [];
                    const existingFilterIndex = normalizedFilters.findIndex(
                        filter =>
                            filter.filterId === filterId &&
                            filter.view?.namespace === view.namespace &&
                            filter.view?.relation === view.relation
                    );
                    if (existingFilterIndex >= 0) {
                        const existing = normalizedFilters[existingFilterIndex];
                        const shouldReplace = !existing?.view?.viewCriteria && !!view?.viewCriteria;
                        if (shouldReplace) {
                            const updatedFilters = [...normalizedFilters];
                            updatedFilters[existingFilterIndex] = { view, filterId };
                            set(listeningFilters.current, [namespace, relation], updatedFilters);
                        }
                    } else {
                        set(listeningFilters.current, [namespace, relation], [
                            ...normalizedFilters,
                            { view, filterId }
                        ]);
                    }
                    viewFiltersChanged = addAnyFiltersNeeded();
                }
                return viewFiltersChanged;
            },
            // Called from the parent DropDown's onChange BEFORE React commits, so
            // dependent dropdowns can clear their display in the same commit as the
            // parent's selection — eliminating the 1-frame flash on React Native
            // where the parent shows the new value but children still show old values.
            clearChildDisplays: (parentNamespace, parentRelation) => {
                const [, publish] = eventSink(eventBoundaryContext);
                const dependentViews = getAllDependentViews(parentNamespace, parentRelation);
                const seenDependents = new Set();
                dependentViews.forEach(view => {
                    const dependentKey = `${view.namespace}:${view.relation}`;
                    if (seenDependents.has(dependentKey)) return;
                    seenDependents.add(dependentKey);
                    publish(
                        {},
                        { verb: 'clearSelection', namespace: view.namespace, relation: view.relation },
                        { preventBubble: true }
                    );
                });
            }
        }),
        // The context instance should never change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    // Create an eventboundary context that is only interested in selection events of children
    // inside this boundary component.
    const eventBoundaryContext = useMemo(
        () => ({
            name,
            subscriptions: {},

            logEvents: !__PRODUCTION__ && !__UNIT_TESTING__,
            parentSink,
            subscriptionFilter: [{ verb: 'select' }, { verb: 'clearSelection' }]
        }),
        [name, parentSink]
    );

    // Listen for selection events - these can trigger changes to cascading/hierarchical filters
    // like foreignRelationSelectionOnForm (e.g. Company > Building > Room dropdown filters)
    // IMPORTANT:
    // This is in a useLayoutEffect because it must happen BEFORE the child controls are rendered.
    // Otherwise, the select events used to create the initial filters will happen too early
    // and the events will bubble up to the parent FilternInterdependencyBoundary instead.
    useLayoutEffect(() => {
        // Subscribe to select events only (see above)
        const [subscribe] = eventSink(eventBoundaryContext);
        logging.debug(`[FilterInterdependencyBoundary] ${name} - creating selection subscription`);
        return subscribe({ verb: 'select', status: 'success' }, (payload, context) => {
            const { namespace, relation } = context;
            logging.debug(
                `[FilterInterdependencyBoundary] ${name} - selection for ${namespace}/${relation}: ${JSON.stringify(
                    payload.value
                )}`
            );

            // Store the new selection and recompute the cascading option filters.
            setSelection(payload.value, namespace, relation);
            addAnyFiltersNeeded();

            // IMPORTANT: do NOT clear child VALUES here.
            // `select:success` fires for ANY value change — user edits AND programmatic ones
            // (record load, navigating between records in the same form, focus/keyboard
            // re-renders, rules-engine oscillations). Clearing children on those wiped the
            // loaded Building/Room when clicking between Assets, and the transient
            // clear-then-restore was the root of the 746 flicker. Every prior fix tried to
            // *guess* from value events whether a change was user-initiated; that guess is
            // unwinnable. Instead, child VALUES are cleared ONLY in response to an explicit
            // USER pick, in the DropDown `onChange` -> `clearChildDisplays` path.
            //
            // The one exception: a MultiPicker parent that becomes EMPTY. MultiPicker has no
            // `onChange` -> `clearChildDisplays` wiring, so we still clear its dependents here.
            if (Array.isArray(payload.value) && payload.value.length === 0) {
                clearSelectionsOnChildDependencies(namespace, relation);
            }
        });

        // This should not change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventBoundaryContext]);

    // Clears dependent child VALUES. Now invoked ONLY for a MultiPicker parent that became
    // empty (see the select:success subscription). Single-select child-clearing is handled by
    // the user-driven clearChildDisplays path; this is intentionally NOT called for single-select
    // programmatic value changes, which is what used to wipe loaded Building/Room when navigating
    // between records.
    function clearSelectionsOnChildDependencies(selectionNamespace, selectionRelation) {
        const [, publish] = eventSink(eventBoundaryContext);
        const dependentViews = getAllDependentViews(selectionNamespace, selectionRelation);
        if (!dependentViews.length) return;

        // If there are child dependencies, clear their selection if the parent
        // selection is changed.
        const parentSelection = getSelection(selectionNamespace, selectionRelation);
        const seenDependents = new Set();
        dependentViews.forEach(view => {
            const dependentKey = `${view.namespace}:${view.relation}`;
            if (seenDependents.has(dependentKey)) return;
            seenDependents.add(dependentKey);

            // MultiPicker:
            if (Array.isArray(parentSelection)) {
                // if parentSelection is empty, publish clearSelection for dependent
                if (parentSelection.length === 0) {
                    publish(
                        {},
                        {
                            verb: 'clearSelection',
                            namespace: view.namespace,
                            relation: view.relation
                        },
                        { preventBubble: true }
                    );
                }
                return;
            }

            // Everything else
            // Only clear if the dependent has a real selection (has _id and is not a default
            // record). This prevents an infinite loop when value={} (empty object without _id)
            // is stored — {} has no isDefaultRecord but also has no real data, so treating
            // it as "needs clearing" would trigger endless re-clearing cycles.
            const dependentSelection = getSelection(view.namespace, view.relation);
            const hasRealSelection = Array.isArray(dependentSelection)
                ? dependentSelection.length > 0
                : dependentSelection?._id && !dependentSelection?.isDefaultRecord;
            if (hasRealSelection) {
                publish(
                    {},
                    { verb: 'clearSelection', namespace: view.namespace, relation: view.relation },
                    { preventBubble: true }
                );
            }
        });
    }

    function getAllDependentViews(parentNamespace, parentRelation) {
        const dependentViews = [];
        const visitedParents = new Set();
        const queue = [[parentNamespace, parentRelation]];

        while (queue.length) {
            const [namespace, relation] = queue.shift();
            const parentKey = `${namespace}:${relation}`;
            if (visitedParents.has(parentKey)) continue;
            visitedParents.add(parentKey);

            const filters = listeningFilters.current[namespace]?.[relation];
            const normalizedFilters = Array.isArray(filters) ? filters : filters ? [filters] : [];

            normalizedFilters.forEach(({ view }) => {
                dependentViews.push(view);
                queue.push([view.namespace, view.relation]);
            });
        }

        return dependentViews;
    }

    /**
     * Based on the selections that have occurred within this filter boundary, look at any views listening
     * for the selection's namespace and relation, and adjust the corresponding filters for those views.
     * @returns boolean - whether any filters changed
     */
    function addAnyFiltersNeeded() {
        let filtersChanged = false;
        const listeners = getAllSelections();
        // Filters based off of selections (like cascading filters (i.e. foreignRelationSelectionOnForm) for cascading dropdowns)
        Object.keys(listeners).forEach(namespace => {
            Object.keys(listeners[namespace]).forEach(relation => {
                const selection = listeners[namespace][relation];
                // Find any listening filters for this selection's namespace and relation
                const filters = listeningFilters.current[namespace]?.[relation];
                const dependentFilters = Array.isArray(filters) ? filters : filters ? [filters] : [];
                dependentFilters.forEach(({ view, filterId }) => {
                    const hasSelection =
                        selection != null && !selection.isDefaultRecord && !selectionIsEmptyArray(selection);
                    // MultiPicker parents hand us an array; single-select parents hand us one record.
                    // A stored selection can be `{}` — an object with no `_id` and no real data (see
                    // clearSelectionsOnChildDependencies below). Dropping those ids leaves `values`
                    // empty, so such a parent constrains nothing, the same as a parent that is null.
                    // Before, `{}` produced a find on `_id: undefined`, which matched only records
                    // missing the field — so a null parent showed every child and a `{}` parent
                    // showed none. Treating the two the same is deliberate.
                    const ids = hasSelection
                        ? []
                              .concat(selection)
                              .map(select => select?._id)
                              .filter(Boolean)
                        : [];
                    // Clearing goes through setFilter with no value rather than removeFilters, so
                    // that path publishes the same 'applyFilters' before-change a set does and the
                    // dbView resets its paging and display gate (#332).
                    const selectionFilter = ids.length
                        ? backboneFilters.foreignRelationSelectionValue.getFilter(`${namespace}:${relation}._id`, ids)
                        : undefined;
                    // setFilter is a no-op (returns false) when the resulting criteria are
                    // unchanged, so a re-render with the same selection does not reset anything.
                    filtersChanged =
                        view.viewCriteria?.setFilter('foreignRelationSelectionValue', selectionFilter, filterId) ||
                        filtersChanged;
                });
            });
        });
        return filtersChanged;
    }

    // Clear filters when unmounting
    useEffect(() => {
        const namespaces = listeningFilters.current;
        return () => {
            // Clear selections unless we need to restore them when remounting
            if (!restoreSelectionsOnRemount) {
                setRouteState(routeStorageKey, {});
            }
            Object.values(namespaces).forEach(relations => {
                Object.values(relations).forEach(filterOrFilters => {
                    const filters = Array.isArray(filterOrFilters) ? filterOrFilters : [filterOrFilters];
                    filters.forEach(filter => {
                        const { view, filterId } = filter;
                        view.viewCriteria?.removeFilters(filterId);
                    });
                });
            });
        };
        // Only do this when unmounting.
        // eslint-disable-next-line
    }, []);

    const retainedParentContext = props.retainParentContext ?? false;
    const parentContext = useContext(FilterInterdependencyBoundaryContext);

    // prettier-ignore
    return rc(FilterInterdependencyBoundaryContext.Provider, { value: retainedParentContext
            ? parentContext
            : filterInterdependencyBoundaryContext
        },
        rc(EventBoundaryContext.Provider, { value: eventBoundaryContext },
            children
        )
    );
}

FilterInterdependencyBoundaryProvider.propTypes = {
    name: PropTypes.string.isRequired
};

export default memo(FilterInterdependencyBoundaryProvider);

function selectionIsEmptyArray(selection) {
    return Array.isArray(selection) && selection.length === 0;
}
